import * as React from 'react';
import type { ScheduledEvent } from 'Utils/VideoEventSchedule';

/**
 * Registers tutorial events as metadata cues on the video element so the
 * media engine — not a JavaScript timer — decides when they fire (MUS-184).
 *
 * The HTML "time marches on" algorithm is edge-triggered by construction:
 * when the playback position advances from A to B the user agent collects
 * every cue starting in that interval, including a dedicated *missed cues*
 * list for cues jumped over entirely, and fires `enter` for all of them. A
 * stalled main thread can therefore delay a cue but cannot drop it, which
 * is precisely the failure the 200ms `onProgress` match window suffered.
 *
 * Cue callbacks are still delivered as main-thread tasks, so a late cue
 * arrives after the playhead has already moved past its timestamp. Callers
 * that pause on an event are responsible for correcting that overshoot.
 */

/** Cues only need a well-formed non-zero span; just the enter edge is used. */
const CUE_DURATION_SECONDS = 0.05;

function supportsMetadataCues(video: HTMLVideoElement | null): video is HTMLVideoElement {
  return (
    video !== null &&
    typeof video.addTextTrack === 'function' &&
    typeof window.VTTCue !== 'undefined'
  );
}

export function useMediaEventCues({
  video,
  schedule,
  onCueEnter,
  onSeeked,
}: {
  video: HTMLVideoElement | null;
  schedule: ScheduledEvent[];
  /** Called with the scheduled event's key when the playhead reaches it. */
  onCueEnter: (key: string) => void;
  /** Called with the landing position after any seek completes. */
  onSeeked: (seconds: number) => void;
}): void {
  // Held in refs so that re-rendering the tutorial does not tear down and
  // rebuild the cue list, which would re-fire cues around the playhead.
  const onCueEnterRef = React.useRef(onCueEnter);
  const onSeekedRef = React.useRef(onSeeked);
  React.useEffect(() => {
    onCueEnterRef.current = onCueEnter;
  }, [onCueEnter]);
  React.useEffect(() => {
    onSeekedRef.current = onSeeked;
  }, [onSeeked]);

  /** A text track cannot be detached from a media element, so reuse one per video. */
  const trackRef = React.useRef<{ video: HTMLVideoElement; track: TextTrack } | null>(null);

  React.useEffect(() => {
    if (!supportsMetadataCues(video) || schedule.length === 0) return;

    if (!trackRef.current || trackRef.current.video !== video) {
      trackRef.current = { video, track: video.addTextTrack('metadata') };
    }
    const { track } = trackRef.current;
    // 'hidden' dispatches cue events without rendering them; 'disabled' would
    // suppress the events entirely.
    track.mode = 'hidden';

    const cues = schedule.map((item) => {
      const cue = new VTTCue(item.at, item.at + CUE_DURATION_SECONDS, item.key);
      cue.id = item.key;
      cue.onenter = () => onCueEnterRef.current(item.key);
      track.addCue(cue);
      return cue;
    });

    return () => {
      cues.forEach((cue) => {
        cue.onenter = null;
        track.removeCue(cue);
      });
      track.mode = 'disabled';
    };
  }, [video, schedule]);

  React.useEffect(() => {
    if (!video) return;
    const handleSeeked = () => onSeekedRef.current(video.currentTime);
    video.addEventListener('seeked', handleSeeked);
    return () => video.removeEventListener('seeked', handleSeeked);
  }, [video]);
}
